import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import * as Sentry from '@sentry/nextjs';
import { ListingState } from '__generated__/athena/graphql';
import type { ZeusResult } from 'components/api/zeusResult';
import { requestZeusFromServerAllowPartialData } from 'components/api/zeusServerClient';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { getDisplayPrice } from 'components/marketplace/helpers/displayPrice';
import { buildGalleryPhotos } from 'components/marketplace/helpers/galleryPhotos';
import { dollarsToCents } from 'components/marketplace/helpers/money';
import { useMarketplaceFavorite } from 'components/marketplace/helpers/useMarketplaceFavorite';
import { isUuid } from 'components/marketplace/helpers/useSearchQueries';
import {
  buildPropertyAnalyticsValue,
  PropertyAnalytics,
  useTrackProperty,
} from 'components/marketplace/helpers/useTrackProperty';
import { GetMarketplacePropertyDetailsDocument } from 'components/marketplace/property/details/__generated__/marketplace-property.zeus';
import type { GetMarketplacePropertyDetailsQuery } from 'components/marketplace/property/details/__generated__/marketplace-property.zeus';
import { mapMarketplaceProperty } from 'components/marketplace/property/details/propertyDetails';
import { GalleryPhotos } from 'components/marketplace/property/media/GalleryPhotos';
import { HeroWalkthroughVideo } from 'components/marketplace/property/media/HeroWalkthroughVideo';
import {
  heroWalkthroughVideoUrl,
  pdpMediaAvailability,
  shouldMountHeroVideo,
} from 'components/marketplace/property/media/pdpEnhancedMedia';
import { PhotoCarousel } from 'components/marketplace/property/media/PhotoCarousel';
import { usePdpMediaSlot } from 'components/marketplace/property/media/usePdpMediaSlot';
import { PropertySection } from 'components/marketplace/property/PropertySection';
import type {
  MarketplaceProperty,
  PropertyDetailsType,
} from 'components/marketplace/property/types';
import MarketplaceLayout from 'components/properties/shared/MarketplaceLayout';
import type {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';
import dynamic from 'next/dynamic';

import { useObservability } from '../../../../helpers/observability';

const Lightbox = dynamic(
  () => import('components/marketplace/property/Lightbox').then((mod) => mod.Lightbox),
  {
    ssr: false,
  },
);

const ShareModal = dynamic(
  () => import('components/marketplace/property/ShareModal').then((mod) => mod.ShareModal),
  {
    ssr: false,
  },
);

const Page: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (props) => {
  const property = props.property;
  const analyticsValue = useMemo(() => buildPropertyAnalyticsValue(property), [property]);
  return (
    <PropertyAnalytics.Provider value={analyticsValue}>
      <PageInner {...props} />
    </PropertyAnalytics.Provider>
  );
};

const PageInner: React.FC<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  property,
}) => {
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const toggleLightbox = useCallback(() => setIsLightboxOpen((prev) => !prev), []);
  // bricks-next `Lightbox` registers a document-level keydown listener that
  // fires `handleClose()` on Escape regardless of `isOpen`, so `handleClose`
  // must be close-only rather than a toggle.
  const closeLightbox = useCallback(() => setIsLightboxOpen(false), []);
  const handleSetSelectedPhotoIndex = useCallback((idx: number) => setSelectedPhotoIndex(idx), []);
  const { isFavoriteLoading, isLiked, onToggleFavorite } = useMarketplaceFavorite({
    addressId: property.addressToken,
    origin: 'LISTING_DETAILS',
    priceCents: property.listPrice == null ? null : dollarsToCents(property.listPrice),
  });
  const handleToggleFavorite = useCallback(() => {
    onToggleFavorite();
  }, [onToggleFavorite]);
  const marketplaceProperty = useMemo<PropertyDetailsType>(
    () => ({
      ...property,
      isFavoriteLoading,
      isLiked,
      onToggleFavorite: handleToggleFavorite,
    }),
    [handleToggleFavorite, isFavoriteLoading, isLiked, property],
  );

  const formattedAddress = useMemo(
    () => formatStreetAddress(property.street, property.unitType, property.unitNumber),
    [property.street, property.unitType, property.unitNumber],
  );

  const carouselPhotos = useMemo(() => buildGalleryPhotos(property), [property]);
  const openShareModal = useCallback(() => setIsShareModalOpen(true), []);
  const closeShareModal = useCallback(() => setIsShareModalOpen(false), []);

  const walkthroughVideoUrl = useMemo(() => heroWalkthroughVideoUrl(property), [property]);
  const availability = useMemo(
    () => pdpMediaAvailability(walkthroughVideoUrl),
    [walkthroughVideoUrl],
  );
  const mediaSlot = usePdpMediaSlot({ addressToken: property.addressToken, availability });
  // Built only when the overlay will actually paint. Keying off the URL alone
  // would hand `GalleryPhotos` a truthy element for a control-arm viewer, whose
  // hero tile would then be stripped of its lightbox click with nothing put in
  // its place -- depressing the arm the readout compares against.
  const heroVideo = useMemo(
    () =>
      shouldMountHeroVideo(walkthroughVideoUrl, mediaSlot.assignedState) && walkthroughVideoUrl
        ? { videoUrl: walkthroughVideoUrl, slot: mediaSlot }
        : null,
    [mediaSlot, walkthroughVideoUrl],
  );
  const heroVideoFill = useMemo(
    () =>
      heroVideo ? (
        <HeroWalkthroughVideo
          key={property.addressToken}
          videoUrl={heroVideo.videoUrl}
          addressToken={property.addressToken}
          addressLabel={formattedAddress}
          slot={heroVideo.slot}
          trigger="fill"
        />
      ) : undefined,
    [formattedAddress, heroVideo, property.addressToken],
  );
  const heroVideoControl = useMemo(
    () =>
      heroVideo ? (
        <HeroWalkthroughVideo
          key={property.addressToken}
          videoUrl={heroVideo.videoUrl}
          addressToken={property.addressToken}
          addressLabel={formattedAddress}
          slot={heroVideo.slot}
          trigger="control"
        />
      ) : undefined,
    [formattedAddress, heroVideo, property.addressToken],
  );

  useEffect(() => {
    trackProperty({ featureName: 'property-view', label: 'marketplace-pdp' });
    trackJourneyEvent('marketplaceProperty', 'detailPageView', {
      addressUuid: property.addressToken,
    });
  }, [property.addressToken, trackJourneyEvent, trackProperty]);

  return (
    <MarketplaceLayout property={marketplaceProperty}>
      <Col className="w-screen overflow-hidden max-md:overflow-y-scroll md:flex-row md:h-[calc(100vh-72px)]">
        <GalleryPhotos
          handleSetSelectedPhotoIndex={handleSetSelectedPhotoIndex}
          property={marketplaceProperty}
          toggleLightbox={toggleLightbox}
          heroOverlay={heroVideoFill}
        />
        <PhotoCarousel
          isAvailableSoon={marketplaceProperty.listingState === ListingState.AVAILABLE_SOON}
          photos={carouselPhotos}
          price={getDisplayPrice(marketplaceProperty, 'purchase')}
          address={formattedAddress}
          toggleLightbox={toggleLightbox}
          setSelectedPhotoIndex={handleSetSelectedPhotoIndex}
          latitude={marketplaceProperty.latitude}
          longitude={marketplaceProperty.longitude}
          openShareModal={openShareModal}
          heroOverlay={heroVideoControl}
        />
        {isLightboxOpen && (
          <Lightbox
            handleClose={closeLightbox}
            isOpen={isLightboxOpen}
            property={marketplaceProperty}
            selectedPhotoIndex={selectedPhotoIndex}
          />
        )}
        <PropertySection property={marketplaceProperty} />
        <ShareModal
          property={marketplaceProperty}
          isOpen={isShareModalOpen}
          onClose={closeShareModal}
        />
      </Col>
    </MarketplaceLayout>
  );
};

export const getServerSideProps: CacheSafeGetServerSideProps<{
  property: MarketplaceProperty;
}> = async (context) => {
  const addressToken = context.query['address_id'];
  if (!addressToken || Array.isArray(addressToken) || !isUuid(addressToken)) {
    return {
      notFound: true,
    };
  }

  if (context.res.destroyed || context.res.writableFinished) {
    return { notFound: true };
  }

  const abortController = new AbortController();
  const abortRequest = () => {
    if (!context.res.writableFinished) {
      abortController.abort();
    }
  };
  context.res.once('close', abortRequest);

  let propertyResult: ZeusResult<GetMarketplacePropertyDetailsQuery>;
  try {
    propertyResult = await requestZeusFromServerAllowPartialData(
      GetMarketplacePropertyDetailsDocument,
      { addressId: addressToken, includeSimilarListings: true },
      { incomingRequestHeaders: context.req.headers, signal: abortController.signal },
    );
  } catch (error: unknown) {
    if (abortController.signal.aborted && error === abortController.signal.reason) {
      // The Sentry loader reports every thrown getServerSideProps error. This response is already
      // unwritable, so resolve without promoting an expected disconnect into an application error.
      return { notFound: true };
    }
    Sentry.captureException(error, {
      tags: { feature: 'marketplace-property', operation: 'GetMarketplacePropertyDetails' },
      extra: { addressToken },
    });
    throw error;
  } finally {
    context.res.off('close', abortRequest);
  }

  if (abortController.signal.aborted) {
    return { notFound: true };
  }

  for (const error of propertyResult.errors) {
    Sentry.captureException(error, {
      tags: { feature: 'marketplace-property', operation: 'GetMarketplacePropertyDetails' },
      extra: { addressToken },
    });
  }
  const property = propertyResult.data ? mapMarketplaceProperty(propertyResult.data) : null;
  if (!property && propertyResult.errors[0]) throw propertyResult.errors[0];

  if (!property) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      property,
    },
  };
};

export default Page;
